import "./styles.css";
import posthog, { captureProcessingFailure, showDownloadSurvey } from "./posthog";
import type { createTrimEditor } from "./trim-editor";
import { clipTime, validTrimSelection } from "./trim-selection";
import { AudioFileError, prepareAudioFile } from "./audio-file";
import { monitorAudioRequest, setAudioRequestHeaders, type RequestPhase } from "./audio-request";
import { isInvalidAudioResponse, transferFailureMessage } from "./audio-response";
import { initSamplePlayer } from "./sample-player";
import { formatBytes } from "./file-size";
import { initVoiceRecorder } from "./voice-recorder";

const isNormalizeAudio = document.body.dataset.tool === "normalize-audio";
const isDatToWav = document.body.dataset.tool === "dat-to-wav";
const isChangeFormat = document.body.dataset.tool === "change-format" || isDatToWav;
const isCompressAudio = document.body.dataset.tool === "compress-audio";
const isTrimAudio = document.body.dataset.tool === "trim-audio";
const isRemoveSilence = document.body.dataset.tool === "remove-silence";
const isChangeSpeed = document.body.dataset.tool === "change-speed";
const isRadioEffect = document.body.dataset.tool === "radio-effect";
const isMegaphoneEffect = document.body.dataset.tool === "megaphone-effect";
const isLoudnessChecker = document.body.dataset.tool === "loudness-checker";
const isSlowedReverb = document.body.dataset.tool === "slowed-reverb";
const isAudioDegrader = document.body.dataset.tool === "audio-degrader";
const isPitchChanger = document.body.dataset.tool === "pitch-changer";
const isReverseAudio = document.body.dataset.tool === "reverse-audio";
const isMuffledAudio = document.body.dataset.tool === "muffled-audio";
const toolConfig = isDatToWav
  ? { endpoint: "/api/dat-to-wav", actionLabel: "Convert to WAV", actionProgressLabel: "Extracting audio from your DAT file…", outputSuffix: "converted", errorLabel: "DAT conversion" }
  : isMuffledAudio
  ? { endpoint: "/api/muffled-audio", actionLabel: "Apply muffled effect", actionProgressLabel: "Adding the muffled sound…", outputSuffix: "muffled-audio", errorLabel: "The muffled effect" }
  : isReverseAudio
  ? { endpoint: "/api/reverse-audio", actionLabel: "Reverse audio", actionProgressLabel: "Reversing your audio…", outputSuffix: "reverse-audio", errorLabel: "Audio reversal" }
  : isAudioDegrader
  ? { endpoint: "/api/audio-degrader", actionLabel: "Degrade audio", actionProgressLabel: "Adding the lo-fi texture…", outputSuffix: "audio-degrader", errorLabel: "Audio degradation" }
  : isPitchChanger
  ? { endpoint: "/api/pitch-changer", actionLabel: "Change pitch", actionProgressLabel: "Changing pitch and preserving duration…", outputSuffix: "pitch-changer", errorLabel: "The pitch change" }
  : isRadioEffect
  ? { endpoint: "/api/radio-effect", actionLabel: "Apply radio effect", actionProgressLabel: "Adding the radio sound…", outputSuffix: "radio-effect", errorLabel: "The radio effect" }
  : isMegaphoneEffect
  ? { endpoint: "/api/megaphone-effect", actionLabel: "Apply megaphone effect", actionProgressLabel: "Adding the megaphone sound…", outputSuffix: "megaphone-effect", errorLabel: "The megaphone effect" }
  : isLoudnessChecker
  ? { endpoint: "/api/loudness-checker", actionLabel: "Check loudness", actionProgressLabel: "Measuring loudness and true peaks…", outputSuffix: "loudness-report", errorLabel: "Loudness analysis" }
  : isSlowedReverb
  ? { endpoint: "/api/slowed-reverb", actionLabel: "Create slowed + reverb", actionProgressLabel: "Slowing the audio and adding reverb…", outputSuffix: "slowed-reverb", errorLabel: "Slowed + reverb" }
  : isCompressAudio
  ? {
      endpoint: "/api/compress-audio",
      actionLabel: "Compress audio",
      actionProgressLabel: "Compressing your audio…",
      outputSuffix: "compressed",
      errorLabel: "Audio compression",
    }
  : isChangeSpeed
  ? {
      endpoint: "/api/change-speed",
      actionLabel: "Change speed",
      actionProgressLabel: "Changing the audio speed…",
      outputSuffix: "speed-changed",
      errorLabel: "The speed change",
    }
  : isRemoveSilence
  ? {
      endpoint: "/api/remove-silence",
      actionLabel: "Remove silence",
      actionProgressLabel: "Removing quiet sections…",
      outputSuffix: "silence-removed",
      errorLabel: "Silence removal",
    }
  : isNormalizeAudio
    ? {
        endpoint: "/api/normalize-audio",
        actionLabel: "Normalize audio",
        actionProgressLabel: "Adjusting the audio loudness…",
        outputSuffix: "normalized",
        errorLabel: "Audio normalization",
      }
  : isChangeFormat
    ? {
        endpoint: "/api/change-format",
        actionLabel: "Change format",
        actionProgressLabel: "Converting your audio…",
        outputSuffix: "converted",
        errorLabel: "The format conversion",
      }
    : isTrimAudio
      ? {
        endpoint: "/api/trim-audio",
        actionLabel: "Download clip",
        actionProgressLabel: "Trimming your audio…",
        outputSuffix: "trimmed",
        errorLabel: "Audio trimming",
      }
      : {
        endpoint: "/api/phone-effect",
        actionLabel: "Apply phone effect",
        actionProgressLabel: "Adding the phone effect…",
        outputSuffix: "phone-effect",
        errorLabel: "The phone effect",
      };

const MAX_AUDIO_BYTES = 100 * 1024 * 1024;
const ACCEPTED_AUDIO = /\.(mp3|wav|ogg|oga|flac|m4a|aac|opus|aif|aiff)$/i;

const fileInput = document.querySelector<HTMLInputElement>("#audio-file")!;
const chooseButton = document.querySelector<HTMLButtonElement>("#choose-file")!;
const changeButton = document.querySelector<HTMLButtonElement>("#change-file")!;
const applyButton = document.querySelector<HTMLButtonElement>("#apply-effect")!;
const downloadButton = document.querySelector<HTMLAnchorElement>("#download-result")!;
const dropzone = document.querySelector<HTMLElement>("#dropzone")!;
const selectedFile = document.querySelector<HTMLElement>("#selected-file")!;
const fileName = document.querySelector<HTMLElement>("#file-name")!;
const fileMeta = document.querySelector<HTMLElement>("#file-meta")!;
const formatSelect = document.querySelector<HTMLSelectElement>("#output-format")!;
const qualitySelect = document.querySelector<HTMLSelectElement>("#output-quality")!;
const qualityField = document.querySelector<HTMLElement>("#quality-field");
const progressRegion = document.querySelector<HTMLElement>("#progress-region")!;
const progressLabel = document.querySelector<HTMLElement>("#progress-label")!;
const progressValue = document.querySelector<HTMLElement>("#progress-value")!;
const progressBar = document.querySelector<HTMLElement>("#progress-bar")!;
const resultRegion = document.querySelector<HTMLElement>("#result")!;
const resultMeta = document.querySelector<HTMLElement>("#result-meta")!;
const audioPreview = document.querySelector<HTMLAudioElement>("#audio-preview")!;
const errorMessage = document.querySelector<HTMLElement>("#error-message")!;
const trimControls = document.querySelector<HTMLElement>("#trim-controls");
const sourcePreview = document.querySelector<HTMLAudioElement>("#source-preview");
const sourcePreviewRegion = document.querySelector<HTMLElement>("#source-preview-region");
const startTime = document.querySelector<HTMLInputElement>("#trim-start");
const endTime = document.querySelector<HTMLInputElement>("#trim-end");
const sourceStatus = document.querySelector<HTMLElement>("#source-status");
const trimDuration = document.querySelector<HTMLElement>("#trim-duration");
const silenceMode = document.querySelector<HTMLSelectElement>("#silence-mode");
const silenceSensitivity = document.querySelector<HTMLSelectElement>("#silence-sensitivity");
const speedInput = document.querySelector<HTMLInputElement>("#speed");
const speedValue = document.querySelector<HTMLOutputElement>("#speed-value");
const speedDuration = document.querySelector<HTMLElement>("#speed-duration");
const compressEstimate = document.querySelector<HTMLElement>("#compress-estimate");
const compressionMode = document.querySelector<HTMLSelectElement>("#compression-mode");
const targetSize = document.querySelector<HTMLInputElement>("#target-size");
const targetSizeField = document.querySelector<HTMLElement>("#target-size-field");
const effectStrength = document.querySelector<HTMLInputElement>("#effect-strength");
const degraderPreset = document.querySelector<HTMLSelectElement>("#degrader-preset");
const muffledPreset = document.querySelector<HTMLSelectElement>("#muffled-preset");
const pitchPreset = document.querySelector<HTMLSelectElement>("#pitch-preset");
const pitchInput = document.querySelector<HTMLInputElement>("#pitch");
const pitchValue = document.querySelector<HTMLOutputElement>("#pitch-value");
const radioPreset = document.querySelector<HTMLSelectElement>("#radio-preset");
const phonePreset = document.querySelector<HTMLSelectElement>("#phone-preset");
const radioNoise = document.querySelector<HTMLInputElement>("#radio-noise");
const radioBeeps = document.querySelector<HTMLInputElement>("#radio-beeps");
const reverbInput = document.querySelector<HTMLInputElement>("#reverb");
const loudnessTarget = document.querySelector<HTMLSelectElement>("#loudness-target");
const sampleButton = document.querySelector<HTMLButtonElement>("#try-sample");
const conversionFormatHelp = document.querySelector<HTMLElement>("#format-help");
const conversionEstimate = document.querySelector<HTMLElement>("#conversion-estimate");
const resultTitle = document.querySelector<HTMLElement>("#result-title");
const resultSettingsNote = document.querySelector<HTMLElement>("#result-settings-note");
let measurements: LoudnessMeasurements | null = null;

interface LoudnessMeasurements {
  integrated_lufs: number | null;
  true_peak_dbtp: number | null;
  loudness_range_lu: number | null;
  duration_seconds: number | null;
  processing_seconds: number;
}

let currentFile: File | null = null;
let resultUrl = "";
let sourceUrl = "";
let sourceDuration = 0;
let estimateDuration = 0;
let estimateUrl = "";
let estimateAudio: HTMLAudioElement | null = null;
let resultSizeBucket = "";
let resultFilename = `${toolConfig.outputSuffix}.mp3`;
let resultFormat = "";
let resultQuality = "";
let activeRequest: XMLHttpRequest | null = null;
let trimEditor: ReturnType<typeof createTrimEditor> | null = null;
let trimEditorLoading: Promise<ReturnType<typeof createTrimEditor>> | null = null;
let pendingFileRead: AbortController | null = null;
let fileSelectionVersion = 0;
let voiceRecorder: ReturnType<typeof initVoiceRecorder> = null;

const analyticsTool = (document.body.dataset.tool || "phone-effect").replace(/-/g, "_");

function sizeBucket(bytes: number): string {
  if (bytes < 1024 * 1024) return "under_1_mb";
  if (bytes < 10 * 1024 * 1024) return "1_to_10_mb";
  if (bytes < 50 * 1024 * 1024) return "10_to_50_mb";
  return "50_to_100_mb";
}

function actionLabel(): string {
  return isChangeFormat ? `Convert to ${formatSelect.value.toUpperCase()}` : toolConfig.actionLabel;
}

function conversionQuality(): string {
  return ["mp3", "ogg"].includes(formatSelect.value) ? qualitySelect.value : "";
}

function updateConversionSettings(): void {
  if (!isChangeFormat) return;
  const descriptions: Record<string, string> = {
    mp3: "Widely supported and easy to share. Uses lossy compression.",
    wav: "Uncompressed 16-bit audio for editing. Usually a larger file.",
    flac: "Lossless compression with a smaller file than WAV. Check your app supports it.",
    ogg: "Compressed audio for apps that support OGG. Uses lossy compression.",
  };
  if (conversionFormatHelp) conversionFormatHelp.textContent = isDatToWav
    ? "16-bit PCM audio. Keeps the first audio track's sample rate and channels. WAV files can be larger than the original."
    : descriptions[formatSelect.value] || "";
  applyButton.querySelector("span")!.textContent = activeRequest ? "Converting…" : actionLabel();
  const matchesResult = resultFormat === formatSelect.value && resultQuality === conversionQuality();
  applyButton.hidden = !activeRequest && Boolean(resultUrl) && matchesResult;
  applyButton.classList.toggle("button-primary", !resultUrl);
  applyButton.classList.toggle("button-secondary", Boolean(resultUrl));
  if (resultTitle) resultTitle.textContent = matchesResult ? "Your converted audio is ready" : "Your previous conversion";
  if (resultSettingsNote) {
    resultSettingsNote.hidden = !resultUrl || matchesResult;
    resultSettingsNote.textContent = activeRequest
      ? `Creating your ${formatSelect.value.toUpperCase()} file. Your previous ${resultFormat.toUpperCase()} download is still available.`
      : `Settings changed. ${actionLabel()} to create a new file. Your ${resultFormat.toUpperCase()} download is still available.`;
  }
  if (conversionEstimate) {
    conversionEstimate.hidden = !currentFile || !estimateDuration || !conversionQuality();
    if (!conversionEstimate.hidden) {
      const estimatedBytes = estimateDuration * Number.parseInt(qualitySelect.value, 10) * 1000 / 8 * 1.02;
      conversionEstimate.textContent = `Estimated output: about ${formatBytes(estimatedBytes)}. Actual size may vary.`;
    }
  }
}

function updateConversionFileMeta(): void {
  if (!isChangeFormat || !currentFile) return;
  const extension = currentFile.name.split(".").pop()?.toLowerCase() || "";
  const format = isDatToWav ? "DAT" : ACCEPTED_AUDIO.test(currentFile.name)
    ? (extension === "oga" ? "OGG" : extension === "aif" ? "AIFF" : extension.toUpperCase())
    : "Audio";
  fileMeta.textContent = [format, ...(estimateDuration > 0 ? [formatTime(estimateDuration)] : []), formatBytes(currentFile.size)].join(" · ");
}

function updateCompressEstimate(): void {
  if (!isCompressAudio || !compressEstimate) return;
  const usesTarget = compressionMode?.value === "target";
  if (targetSizeField) targetSizeField.hidden = !usesTarget;
  if (qualityField) qualityField.hidden = usesTarget;
  if (usesTarget) {
    compressEstimate.textContent = `Size limit: ${targetSize?.value || "your chosen"} MB. A very small limit may need a shorter recording.`;
    return;
  }
  if (!currentFile) {
    compressEstimate.textContent = "Choose a file to see its estimated output size.";
    return;
  }
  if (!estimateDuration) {
    compressEstimate.textContent = "Reading the recording length to estimate its size…";
    return;
  }
  const bitrate = Number.parseInt(qualitySelect.value, 10) * 1000;
  const estimatedBytes = estimateDuration * bitrate / 8 * 1.02;
  const comparison = estimatedBytes < currentFile.size
    ? `about ${Math.round((1 - estimatedBytes / currentFile.size) * 100)}% smaller than the original`
    : "may be larger than the original";
  compressEstimate.textContent = `Estimated output: about ${formatBytes(estimatedBytes)} · ${comparison}. Actual size may vary.`;
}

function resetResult(): void {
  audioPreview.pause();
  if (resultUrl) URL.revokeObjectURL(resultUrl);
  resultUrl = "";
  resultSizeBucket = "";
  resultFormat = "";
  resultQuality = "";
  resultRegion.hidden = true;
  downloadButton.hidden = true;
  downloadButton.removeAttribute("href");
  audioPreview.removeAttribute("src");
  errorMessage.hidden = true;
  errorMessage.textContent = "";
  measurements = null;
  updateConversionSettings();
}

async function setFile(file: File, fromRecording = false): Promise<void> {
  if (activeRequest || (voiceRecorder?.busy && !fromRecording)) return;
  if (isDatToWav ? !/\.dat$/i.test(file.name) : !(file.type.startsWith("audio/") || ACCEPTED_AUDIO.test(file.name))) {
    showError(isDatToWav ? "Choose a .dat file containing audio, such as a VCD recording."
      : "Choose a supported audio file: MP3, WAV, OGG, FLAC, M4A, AAC, OPUS, AIFF, or AIF.");
    return;
  }
  const selectionVersion = ++fileSelectionVersion;
  pendingFileRead?.abort();
  const controller = new AbortController();
  pendingFileRead = controller;
  // Start reading before accessing size or creating previews on Android.
  const preparedFile = prepareAudioFile(file, MAX_AUDIO_BYTES, /Android/i.test(navigator.userAgent), controller.signal);
  currentFile = null;
  if (sourcePreviewRegion) sourcePreviewRegion.hidden = true;
  sourcePreview?.pause();
  resetResult();
  applyButton.disabled = true;
  setBusy(true);
  setProgress("Reading audio…", isChangeFormat ? null : 4);
  try {
    file = await preparedFile;
  } catch (error) {
    if (selectionVersion !== fileSelectionVersion) return;
    pendingFileRead = null;
    setBusy(false);
    const reason = error instanceof AudioFileError ? error.reason : "unreadable";
    if (reason === "unreadable") captureProcessingFailure("file_read_error", { tool: analyticsTool });
    showError(reason === "too_large" ? "This file is larger than the 100 MB limit."
      : reason === "empty" ? "This audio file is empty. Choose another file."
      : "We could not read this audio file. Save it to your device, then choose it again.");
    return;
  }
  if (selectionVersion !== fileSelectionVersion) return;
  pendingFileRead = null;
  setBusy(false);
  progressRegion.hidden = true;
  currentFile = file;
  trimEditor?.reset();
  if (isDatToWav) estimateDuration = 0;
  if (isCompressAudio || (isChangeFormat && !isDatToWav)) {
    if (estimateUrl) URL.revokeObjectURL(estimateUrl);
    estimateDuration = 0;
    estimateAudio?.removeAttribute("src");
    estimateAudio = new Audio();
    estimateAudio.preload = "metadata";
    estimateUrl = URL.createObjectURL(file);
    estimateAudio.addEventListener("loadedmetadata", () => {
      if (currentFile !== file) return;
      estimateDuration = Number.isFinite(estimateAudio?.duration) ? estimateAudio!.duration : 0;
      if (estimateDuration > 0) {
        updateCompressEstimate();
        updateConversionFileMeta();
        updateConversionSettings();
      } else if (compressEstimate) {
        compressEstimate.textContent = "The output size depends on the audio duration and selected bitrate.";
      }
    }, { once: true });
    estimateAudio.addEventListener("error", () => {
      if (currentFile === file && compressEstimate) {
        compressEstimate.textContent = "The output size depends on the audio duration and selected bitrate.";
      }
    }, { once: true });
    estimateAudio.src = estimateUrl;
    estimateAudio.load();
    updateCompressEstimate();
  }
  if (sourcePreview) {
    sourcePreview.pause();
    if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    sourceUrl = URL.createObjectURL(file);
    sourcePreview.src = sourceUrl;
    sourcePreview.load();
    if (sourcePreviewRegion) sourcePreviewRegion.hidden = false;
    if (isChangeSpeed || isSlowedReverb) {
      sourceDuration = 0;
      updateSpeedDisplay();
    }
  }
  if (isTrimAudio && startTime && endTime && trimControls) {
    sourceDuration = 0;
    startTime.value = "0";
    startTime.removeAttribute("max");
    endTime.value = "";
    endTime.removeAttribute("max");
    endTime.placeholder = "Enter end time";
    if (sourceStatus) sourceStatus.textContent = "Reading the recording length…";
    trimControls.hidden = false;
    updateTrimSelection();
  }
  dropzone.hidden = true;
  selectedFile.hidden = false;
  applyButton.hidden = false;
  fileName.textContent = file.name;
  fileMeta.textContent = `${formatBytes(file.size)} · ${file.type || "Audio file"}`;
  updateConversionFileMeta();
  applyButton.disabled = isTrimAudio ? true : false;
  if (isTrimAudio) updateTrimSelection();
  applyButton.querySelector("span")!.textContent = actionLabel();
  updateConversionSettings();
  if (isChangeFormat) changeButton.focus({ preventScroll: true });
  if (isTrimAudio) {
    const extension = file.name.split(".").pop()?.toLowerCase() || "";
    const originalFormat = extension === "oga" ? "ogg" : extension;
    formatSelect.value = ["mp3", "wav", "flac", "ogg"].includes(originalFormat) ? originalFormat : "mp3";
    formatSelect.dispatchEvent(new Event("change"));
    const url = sourceUrl;
    document.querySelector<HTMLDetailsElement>(".trim-record-option")!.open = false;
    try {
      const editor = await ensureTrimEditor();
      // A different file may have been chosen while the editor was loading.
      if (selectionVersion === fileSelectionVersion) await editor.load(url);
    } catch {
      if (selectionVersion !== fileSelectionVersion) return;
      sourceStatus!.textContent = "The waveform could not load. You can still cut your audio using the start and end times.";
      sourceStatus!.hidden = false;
      sourcePreview!.hidden = false;
      sourcePreview!.controls = true;
    }
  }
}

function showError(message: string): void {
  errorMessage.textContent = message;
  errorMessage.hidden = false;
  progressRegion.hidden = true;
  applyButton.disabled = !currentFile;
  updateConversionSettings();
  applyButton.querySelector("span")!.textContent = isChangeFormat ? `Retry conversion to ${formatSelect.value.toUpperCase()}` : "Try again";
}

function setProgress(label: string, percent: number | null): void {
  progressRegion.hidden = false;
  progressLabel.textContent = label;
  progressRegion.classList.toggle("is-indeterminate", percent === null);
  progressValue.textContent = percent === null ? "" : `${Math.round(percent)}%`;
  progressBar.style.width = percent === null ? "35%" : `${Math.max(2, Math.min(100, percent))}%`;
}

function updateTrimSelection(): void {
  if (!isTrimAudio || !startTime || !endTime) return;
  const start = Number(startTime.value);
  const end = Number(endTime.value);
  const valid = validTrimSelection(startTime.value, endTime.value, sourceDuration);
  if (trimDuration) {
    trimDuration.textContent = valid
      ? clipTime(end - start)
      : end <= start && endTime.value !== "" ? "The end must come after the start."
      : sourceDuration && end > sourceDuration + 0.005 ? "The end must be within this recording."
      : "Choose a clip of at least 0.1 seconds.";
    trimDuration.setAttribute("aria-label", valid ? `Selected clip: ${Number((end - start).toFixed(2))} seconds` : trimDuration.textContent);
  }
  for (const input of [startTime, endTime]) input.setAttribute("aria-invalid", String(!valid && input.value !== ""));
  applyButton.disabled = !currentFile || !valid;
  trimEditor?.syncSelection();
}

function formatTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${(seconds % 60).toFixed(1).padStart(4, "0")}`;
}

function updateSpeedDisplay(): void {
  if ((!isChangeSpeed && !isSlowedReverb) || !speedInput) return;
  const speed = Number(speedInput.value);
  if (speedValue) speedValue.textContent = `${speed.toFixed(2)}×`;
  if (speedDuration) {
    speedDuration.textContent = sourceDuration > 0
      ? `Estimated result: ${formatTime(sourceDuration / speed + (isSlowedReverb && Number(reverbInput?.value) > 0 ? 2 : 0))} from ${formatTime(sourceDuration)}${isSlowedReverb && Number(reverbInput?.value) > 0 ? " · includes the reverb trail" : ""}`
      : isSlowedReverb ? "Slowing the audio also lowers its pitch." : "The pitch stays close to the original.";
  }
}

function setBusy(busy: boolean): void {
  for (const control of document.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLButtonElement>(".settings-panel input, .settings-panel select, .trim-fields input, .trim-fields button, #output-format, #audio-file, #change-file, #choose-file, #try-sample, .sample-play, .sample-seek, [data-sample-version], [data-sample-select]")) {
    control.disabled = busy;
  }
  voiceRecorder?.setDisabled(busy);
  if (!busy) qualitySelect.disabled = isLoudnessChecker || !["mp3", "ogg"].includes(formatSelect.value);
  trimEditor?.setBusy(busy);
}

function updateLoudnessReport(): void {
  if (!measurements) return;
  const { integrated_lufs: loudness, true_peak_dbtp: peak, loudness_range_lu: range } = measurements;
  const display = (id: string, value: number | null, unit: string) => {
    const element = document.querySelector<HTMLElement>(id);
    if (element) element.textContent = value === null ? "Not measurable" : `${value.toFixed(1)} ${unit}`;
  };
  display("#measured-loudness", loudness, "LUFS");
  display("#measured-peak", peak, "dBTP");
  display("#measured-range", range, "LU");
  const summary = document.querySelector<HTMLElement>("#loudness-summary");
  if (summary) {
    const difference = loudness === null ? null : Number(loudnessTarget?.value || -16) - loudness;
    const comparison = difference === null ? "There is too little audible sound to measure integrated loudness."
      : Math.abs(difference) < 0.5 ? "Your audio is close to the selected loudness target."
      : `Your audio is ${Math.abs(difference).toFixed(1)} LU ${difference > 0 ? "quieter" : "louder"} than the selected target.`;
    summary.textContent = comparison + (peak !== null && peak > -1 ? " True peaks are close to or above 0 dBTP; leave more headroom before exporting." : "");
  }
}

function outputFilename(response: XMLHttpRequest, file: File, format: string): string {
  const disposition = response.getResponseHeader("content-disposition") || "";
  const match = disposition.match(/filename="?([^";]+)"?/i);
  if (match?.[1]) return match[1];
  const stem = file.name.replace(/\.[^.]+$/, "");
  return `${stem}-${toolConfig.outputSuffix}.${format}`;
}

function processAudio(): void {
  const inputFile = currentFile;
  if (!inputFile || activeRequest || pendingFileRead || voiceRecorder?.busy) return;
  if (isTrimAudio && applyButton.disabled) return;
  if (isTrimAudio && resultUrl) {
    downloadButton.click();
    return;
  }
  if (isCompressAudio && compressionMode?.value === "target" && !targetSize?.reportValidity()) return;
  const requestedFormat = formatSelect.value;
  const requestedQuality = qualitySelect.value;
  const requestedSpeed = (isChangeSpeed || isSlowedReverb) && speedInput ? Number(speedInput.value) : 0;
  const analyticsProperties = {
    tool: analyticsTool,
    output_format: isLoudnessChecker ? "json" : requestedFormat,
    output_quality: compressionMode?.value === "target" ? "automatic" : requestedQuality,
    input_size_bucket: sizeBucket(inputFile.size),
    ...(isRemoveSilence && silenceMode && silenceSensitivity
      ? { silence_mode: silenceMode.value, silence_sensitivity: silenceSensitivity.value }
      : {}),
    ...((isChangeSpeed || isSlowedReverb) && speedInput ? { speed: requestedSpeed } : {}),
    ...(effectStrength ? { effect_strength: Number(effectStrength.value) } : {}),
    ...(degraderPreset ? { degrader_preset: degraderPreset.value } : {}),
    ...(muffledPreset ? { muffled_preset: muffledPreset.value } : {}),
    ...(phonePreset ? { phone_preset: phonePreset.value } : {}),
    ...(pitchInput ? { pitch_semitones: Number(pitchInput.value) } : {}),
    ...(radioPreset ? { radio_preset: radioPreset.value, radio_noise: radioNoise?.checked, radio_beeps: radioBeeps?.checked } : {}),
    ...(reverbInput ? { reverb_amount: Number(reverbInput.value) } : {}),
    ...(isCompressAudio ? { compression_mode: compressionMode?.value || "bitrate", ...(compressionMode?.value === "target" ? { target_mb: Number(targetSize?.value) } : {}) } : {}),
  };
  if (isChangeFormat && resultUrl) {
    audioPreview.pause();
    errorMessage.hidden = true;
    errorMessage.textContent = "";
    if (resultSettingsNote) resultSettingsNote.textContent = `Creating your ${requestedFormat.toUpperCase()} file. Your previous ${resultFormat.toUpperCase()} download is still available.`;
  } else {
    resetResult();
  }
  applyButton.disabled = true;
  setBusy(true);
  applyButton.querySelector("span")!.textContent = isChangeFormat ? "Converting…" : `${toolConfig.actionLabel}…`;
  setProgress("Uploading audio…", isChangeFormat ? 0 : 4);

  const query = new URLSearchParams({ format: requestedFormat, quality: requestedQuality });
  if (isTrimAudio && startTime && endTime) {
    query.set("start", startTime.value);
    query.set("end", endTime.value);
  }
  if (isRemoveSilence && silenceMode && silenceSensitivity) {
    query.set("mode", silenceMode.value);
    query.set("sensitivity", silenceSensitivity.value);
  }
  if ((isChangeSpeed || isSlowedReverb) && speedInput) query.set("speed", speedInput.value);
  if (effectStrength) query.set("strength", effectStrength.value);
  if (degraderPreset) query.set("preset", degraderPreset.value);
  if (muffledPreset) query.set("preset", muffledPreset.value);
  if (phonePreset) query.set("preset", phonePreset.value);
  if (pitchInput) query.set("semitones", pitchInput.value);
  if (radioPreset) {
    query.set("preset", radioPreset.value);
    query.set("noise", String(radioNoise?.checked || false));
    query.set("beeps", String(radioBeeps?.checked || false));
  }
  if (reverbInput) query.set("reverb", reverbInput.value);
  if (compressionMode) query.set("compression_mode", compressionMode.value);
  if (targetSize && compressionMode?.value === "target") query.set("target_mb", targetSize.value);
  const request = new XMLHttpRequest();
  const processingTimeoutMs = (isRemoveSilence || compressionMode?.value === "target" ? 12 : 7) * 60 * 1000;
  const timeoutMessages: Record<RequestPhase, string> = {
    upload: "The upload stalled. Check your connection and try again.",
    processing: "The audio service took too long to respond. Try again or use a shorter file.",
    download: transferFailureMessage("download", requestedFormat),
  };
  const monitor = monitorAudioRequest(request, processingTimeoutMs, (phase) => {
    activeRequest = null;
    setBusy(false);
    captureProcessingFailure("timeout", { ...analyticsProperties, ...monitor.diagnostics() });
    showError(timeoutMessages[phase]);
  });
  activeRequest = request;
  request.open("POST", `${toolConfig.endpoint}?${query}`);
  request.responseType = "blob";
  setAudioRequestHeaders(request, inputFile);

  request.upload.onprogress = (event) => {
    if (!event.lengthComputable) return;
    setProgress("Uploading audio…", isChangeFormat ? (event.loaded / event.total) * 100 : Math.min(72, (event.loaded / event.total) * 72));
  };
  request.upload.onload = () => setProgress(isChangeFormat ? "Converting your audio…" : "Starting the audio processor…", isChangeFormat ? null : 78);
  request.onreadystatechange = () => {
    if (request.readyState === XMLHttpRequest.HEADERS_RECEIVED) {
      setProgress(isChangeFormat ? "Receiving converted audio…" : toolConfig.actionProgressLabel, isChangeFormat ? null : 90);
    }
  };
  if (isChangeFormat) {
    request.onprogress = (event) => setProgress("Receiving converted audio…", event.lengthComputable ? (event.loaded / event.total) * 100 : null);
  }
  request.onload = async () => {
    if (request.status < 200 || request.status >= 300) {
      let message = `${toolConfig.errorLabel} could not be completed.`;
      let invalidAudio = false;
      try {
        const payload = JSON.parse(await request.response.text()) as { error?: string };
        if (typeof payload?.error === "string") message = payload.error || message;
        invalidAudio = isInvalidAudioResponse(request.status, payload);
      } catch {
        // Keep the friendly fallback.
      }
      captureProcessingFailure(invalidAudio ? "invalid_audio" : "processor_error", {
        ...analyticsProperties,
        ...monitor.diagnostics(),
        http_status: request.status,
      });
      activeRequest = null;
      setBusy(false);
      showError(message);
      return;
    }

    if (isLoudnessChecker) {
      try {
        measurements = JSON.parse(await request.response.text()) as LoudnessMeasurements;
        updateLoudnessReport();
        resultUrl = URL.createObjectURL(request.response);
        resultSizeBucket = sizeBucket(request.response.size);
        resultFilename = "loudness-report.json";
        downloadButton.href = resultUrl;
        downloadButton.download = resultFilename;
        downloadButton.querySelector("span")!.textContent = `Download JSON report · ${formatBytes(request.response.size)}`;
        resultMeta.textContent = measurements.duration_seconds ? `${formatTime(measurements.duration_seconds)} · Original file unchanged` : "Original file unchanged";
        resultRegion.hidden = false;
        downloadButton.hidden = false;
        applyButton.hidden = true;
        if (document.activeElement === applyButton || document.activeElement === document.body) {
          downloadButton.focus({ preventScroll: true });
        }
        posthog.capture("audio_processing_completed", { ...analyticsProperties, processing_seconds: measurements.processing_seconds });
      } catch {
        captureProcessingFailure("processor_error", analyticsProperties);
        showError("We could not read the loudness report. Try again.");
      }
      activeRequest = null;
      setBusy(false);
      progressRegion.hidden = true;
      return;
    }

    setProgress("Finishing your audio…", 100);
    if (resultUrl) URL.revokeObjectURL(resultUrl);
    resultUrl = URL.createObjectURL(request.response);
    resultSizeBucket = sizeBucket(request.response.size);
    resultFormat = requestedFormat;
    resultQuality = ["mp3", "ogg"].includes(requestedFormat) ? requestedQuality : "";
    resultFilename = outputFilename(request, inputFile, requestedFormat);
    downloadButton.href = resultUrl;
    downloadButton.download = resultFilename;
    downloadButton.querySelector("span")!.textContent = `Download ${resultFormat.toUpperCase()} · ${formatBytes(request.response.size)}`;
    audioPreview.src = resultUrl;
    const processingSeconds = Number(request.getResponseHeader("x-total-processing-seconds") || "0");
    const durationSeconds = Number(request.getResponseHeader("x-duration-seconds") || "0");
    const sourceDurationSeconds = Number(request.getResponseHeader("x-source-duration-seconds") || "0");
    const parts = [requestedFormat.toUpperCase(), formatBytes(request.response.size)];
    if (isReverseAudio) parts.push("Plays backwards");
    if (muffledPreset && effectStrength) parts.push(`${muffledPreset.selectedOptions[0]?.text || muffledPreset.value} · ${Math.round(Number(effectStrength.value) * 100)}% depth`);
    if (isChangeFormat && resultQuality) parts.push(`${Number.parseInt(resultQuality, 10)} kbps`);
    if (isChangeSpeed || isSlowedReverb) parts.push(`${requestedSpeed.toFixed(2)}× speed`);
    if (isPitchChanger && pitchInput) parts.push(`${Number(pitchInput.value) > 0 ? "+" : ""}${pitchInput.value} semitones · original duration`);
    if (isAudioDegrader && degraderPreset && effectStrength) parts.push(`${degraderPreset.selectedOptions[0]?.text || degraderPreset.value} · ${Math.round(Number(effectStrength.value) * 100)}% strength`);
    if (phonePreset) parts.push(`${phonePreset.selectedOptions[0]?.text || phonePreset.value}${effectStrength ? ` · ${Math.round(Number(effectStrength.value) * 100)}% strength` : ""}`);
    if (durationSeconds > 0) {
      parts.push(isTrimAudio || isRemoveSilence || isChangeSpeed ? `${durationSeconds.toFixed(1)} sec` : `${Math.round(durationSeconds)} sec`);
    }
    if (isRemoveSilence && sourceDurationSeconds > 0 && durationSeconds > 0) {
      const removedSeconds = sourceDurationSeconds - durationSeconds;
      parts.push(removedSeconds > 0.05 ? `${removedSeconds.toFixed(1)} sec removed` : "No long quiet sections found");
    }
    if (isCompressAudio && inputFile.size > 0) {
      const difference = Math.round((1 - request.response.size / inputFile.size) * 100);
      parts.push(difference > 0 ? `${difference}% smaller` : difference < 0 ? `${Math.abs(difference)}% larger` : "similar size");
    }
    if (processingSeconds > 0) parts.push(`processed in ${processingSeconds.toFixed(1)} sec`);
    posthog.capture("audio_processing_completed", {
      ...analyticsProperties,
      processing_seconds: processingSeconds,
      output_size_bucket: resultSizeBucket,
    });
    resultMeta.textContent = parts.join(" · ");
    progressRegion.hidden = true;
    resultRegion.hidden = false;
    downloadButton.hidden = false;
    applyButton.hidden = !isTrimAudio;
    activeRequest = null;
    setBusy(false);
    if (isChangeFormat) {
      if (!estimateDuration) estimateDuration = sourceDurationSeconds || durationSeconds;
      updateConversionFileMeta();
      updateConversionSettings();
    }
    if (!isTrimAudio && (document.activeElement === applyButton || document.activeElement === document.body)) {
      downloadButton.focus({ preventScroll: true });
    }
    if (isTrimAudio) {
      applyButton.querySelector("span")!.textContent = "Download clip";
      updateTrimSelection();
      downloadButton.click();
    }
  };
  request.onerror = () => {
    activeRequest = null;
    setBusy(false);
    captureProcessingFailure("network_error", { ...analyticsProperties, ...monitor.diagnostics(), connection_online: navigator.onLine });
    showError(transferFailureMessage(monitor.diagnostics().request_phase, requestedFormat));
  };
  // Upload and download use progress-aware limits; processing gets its own budget.
  request.timeout = 0;
  monitor.start();
  posthog.capture("audio_processing_started", analyticsProperties);
  try {
    request.send(inputFile);
  } catch {
    monitor.finish();
    request.abort();
    activeRequest = null;
    setBusy(false);
    captureProcessingFailure("network_error", { ...analyticsProperties, ...monitor.diagnostics(), connection_online: navigator.onLine });
    showError("We could not upload this audio file. Save it to your device, then choose it again.");
  }
}

function ensureTrimEditor(): Promise<ReturnType<typeof createTrimEditor>> {
  if (trimEditor) return Promise.resolve(trimEditor);
  if (trimEditorLoading) return trimEditorLoading;
  // Keep the waveform library off the initial page load and every other tool.
  trimEditorLoading = import("./trim-editor").then(({ createTrimEditor }) => {
    trimEditor = createTrimEditor({
      audio: sourcePreview!,
      startInput: startTime!,
      endInput: endTime!,
      onDuration(duration) {
        const followedSourceEnd = sourceDuration > 0 && Math.abs(Number(endTime!.value) - sourceDuration) < 0.01;
        sourceDuration = duration;
        const roundedSeconds = Math.round(duration);
        fileMeta.textContent = duration >= 60
          ? `${Math.floor(roundedSeconds / 60)} min ${roundedSeconds % 60} sec`
          : `${Number(duration.toFixed(1))} sec`;
        if (!endTime!.value || followedSourceEnd) endTime!.value = duration.toFixed(2);
        endTime!.max = startTime!.max = duration.toFixed(2);
        updateTrimSelection();
      },
      onSelectionChange() {
        resetResult();
        applyButton.hidden = false;
        applyButton.querySelector("span")!.textContent = toolConfig.actionLabel;
        updateTrimSelection();
      },
    });
    return trimEditor;
  }).catch((error) => {
    trimEditorLoading = null;
    throw error;
  });
  return trimEditorLoading;
}

function chooseFile(): void {
  fileInput.click();
}

chooseButton.addEventListener("click", (event) => {
  event.stopPropagation();
  chooseFile();
});
changeButton.addEventListener("click", chooseFile);
fileInput.addEventListener("change", () => {
  const file = fileInput.files?.[0];
  if (file) setFile(file);
});

for (const eventName of ["dragenter", "dragover"]) {
  window.addEventListener(eventName, (event) => {
    event.preventDefault();
    dropzone.classList.add("is-dragging");
  });
}
for (const eventName of ["dragleave", "drop"]) {
  window.addEventListener(eventName, (event) => {
    event.preventDefault();
    dropzone.classList.remove("is-dragging");
  });
}
window.addEventListener("drop", (event) => {
  const file = event.dataTransfer?.files[0];
  if (file) setFile(file);
});

function settingsChanged(): void {
  if (isChangeFormat) {
    errorMessage.hidden = true;
    errorMessage.textContent = "";
    applyButton.disabled = !currentFile;
    updateConversionSettings();
    return;
  }
  resetResult();
  applyButton.hidden = false;
  applyButton.disabled = !currentFile;
  applyButton.querySelector("span")!.textContent = toolConfig.actionLabel;
  if (isTrimAudio) updateTrimSelection();
  if (isCompressAudio) updateCompressEstimate();
}

formatSelect.addEventListener("change", () => {
  const supportsBitrate = formatSelect.value === "mp3" || formatSelect.value === "ogg";
  for (const option of qualitySelect.options) {
    const unsupported = formatSelect.value === "ogg" && Number.parseInt(option.value, 10) > 192;
    option.disabled = unsupported;
    option.hidden = unsupported;
  }
  if (formatSelect.value === "ogg" && Number.parseInt(qualitySelect.value, 10) > 192) qualitySelect.value = "192k";
  qualitySelect.disabled = !supportsBitrate;
  if (qualityField) qualityField.hidden = !supportsBitrate;
  if (isTrimAudio) {
    const extension = currentFile?.name.split(".").pop()?.toLowerCase();
    const originalFormat = extension === "oga" ? "ogg" : extension;
    const note = document.querySelector<HTMLElement>("#trim-format-note");
    if (note) note.textContent = `${formatSelect.value.toUpperCase()}${originalFormat === formatSelect.value ? " · same as original" : " clip"}`;
  }
  settingsChanged();
});
qualitySelect.addEventListener("change", settingsChanged);
compressionMode?.addEventListener("change", settingsChanged);
targetSize?.addEventListener("input", settingsChanged);
radioPreset?.addEventListener("change", settingsChanged);
phonePreset?.addEventListener("change", settingsChanged);
degraderPreset?.addEventListener("change", settingsChanged);
muffledPreset?.addEventListener("change", settingsChanged);
function updatePitchDisplay(): void {
  if (!pitchInput || !pitchValue) return;
  const value = Number(pitchInput.value);
  pitchValue.textContent = `${value > 0 ? "+" : ""}${value} semitones`;
  pitchInput.setAttribute("aria-valuetext", value === 0 ? "Original pitch" : `${Math.abs(value)} semitones ${value < 0 ? "lower" : "higher"}`);
  if (pitchPreset) pitchPreset.value = ["0", "-5", "5"].includes(pitchInput.value) ? pitchInput.value : "custom";
}
pitchPreset?.addEventListener("change", () => {
  if (!pitchInput || pitchPreset.value === "custom") return;
  pitchInput.value = pitchPreset.value;
  updatePitchDisplay();
  settingsChanged();
});
pitchInput?.addEventListener("input", () => {
  updatePitchDisplay();
  settingsChanged();
});
radioNoise?.addEventListener("change", settingsChanged);
radioBeeps?.addEventListener("change", settingsChanged);
for (const input of [effectStrength, reverbInput]) {
  input?.addEventListener("input", () => {
    const output = document.querySelector<HTMLOutputElement>(`#${input.id}-value`);
    if (output) output.textContent = `${Math.round(Number(input.value) * 100)}%`;
    if (input === reverbInput) updateSpeedDisplay();
    settingsChanged();
  });
}
loudnessTarget?.addEventListener("change", updateLoudnessReport);
sampleButton?.addEventListener("click", async () => {
  if (activeRequest || voiceRecorder?.busy) return;
  const selectionVersion = fileSelectionVersion;
  const label = sampleButton.innerHTML;
  sampleButton.disabled = true;
  sampleButton.textContent = "Loading sample…";
  try {
    const path = isAudioDegrader ? "/samples/degrader-original.mp3?v=1"
      : isPitchChanger ? "/samples/pitch-original.mp3?v=1"
      : isReverseAudio ? "/samples/reverse-original.mp3?v=1"
      : isMuffledAudio ? "/samples/muffled-original.mp3?v=2"
      : isSlowedReverb ? "/samples/slowed-original.mp3?v=2"
      : "/samples/voice.wav";
    const response = await fetch(path);
    if (!response.ok) throw new Error("Sample unavailable");
    const blob = await response.blob();
    if (!activeRequest && !voiceRecorder?.busy && fileSelectionVersion === selectionVersion) {
      const filename = new URL(path, window.location.href).pathname.split("/").pop() || "sample-voice.wav";
      await setFile(new File([blob], filename, { type: blob.type || (isAudioDegrader || isPitchChanger || isReverseAudio || isMuffledAudio || isSlowedReverb ? "audio/mpeg" : "audio/wav") }));
    }
  } catch {
    if (fileSelectionVersion === selectionVersion) showError("We could not load the sample. Choose an audio file to continue.");
  } finally {
    sampleButton.innerHTML = label;
    sampleButton.disabled = Boolean(activeRequest || pendingFileRead || voiceRecorder?.busy);
  }
});
silenceMode?.addEventListener("change", settingsChanged);
silenceSensitivity?.addEventListener("change", settingsChanged);
speedInput?.addEventListener("input", () => {
  updateSpeedDisplay();
  settingsChanged();
});
if ((isChangeSpeed || isSlowedReverb) && sourcePreview) {
  sourcePreview.addEventListener("loadedmetadata", () => {
    sourceDuration = Number.isFinite(sourcePreview.duration) ? sourcePreview.duration : 0;
    updateSpeedDisplay();
  });
}
if (isTrimAudio && startTime && endTime) {
  for (const input of [startTime, endTime]) {
    input.addEventListener("input", () => {
      trimEditor?.pause();
      resetResult();
      applyButton.hidden = false;
      applyButton.querySelector("span")!.textContent = toolConfig.actionLabel;
      updateTrimSelection();
    });
  }
}
applyButton.addEventListener("click", processAudio);
downloadButton.addEventListener("click", (event) => {
  if (!resultUrl) {
    event.preventDefault();
    return;
  }
  posthog.capture(isLoudnessChecker ? "audio_report_downloaded" : "audio_result_downloaded", {
    tool: analyticsTool,
    output_format: isLoudnessChecker ? "json" : resultFormat || formatSelect.value,
    output_size_bucket: resultSizeBucket,
  });
  if (!isLoudnessChecker) showDownloadSurvey();
});

updateCompressEstimate();
updateConversionSettings();
updatePitchDisplay();
initSamplePlayer();
voiceRecorder = initVoiceRecorder({
  onBusy(busy) {
    if (busy) {
      ++fileSelectionVersion; // A sample fetch started earlier must not replace the recording.
      for (const audio of document.querySelectorAll<HTMLAudioElement>("audio")) audio.pause();
    }
    setBusy(busy);
    applyButton.disabled = busy || !currentFile;
    if (!busy && isTrimAudio) updateTrimSelection();
  },
  async onRecording(file) {
    await setFile(file, true);
    if (isTrimAudio) document.querySelector<HTMLButtonElement>("#play-full")?.focus({ preventScroll: true });
    else sourcePreview?.focus({ preventScroll: true });
  },
});
document.addEventListener("play", (event) => {
  if (voiceRecorder?.busy && event.target instanceof HTMLMediaElement) event.target.pause();
}, true);
if (isTrimAudio) {
  chooseButton.disabled = false;
  fileInput.disabled = false;
}
document.querySelector<HTMLElement>("#year")!.textContent = String(new Date().getFullYear());
window.addEventListener("beforeunload", () => {
  trimEditor?.destroy();
  if (sourceUrl) URL.revokeObjectURL(sourceUrl);
  if (resultUrl) URL.revokeObjectURL(resultUrl);
  if (estimateUrl) URL.revokeObjectURL(estimateUrl);
});
